import React, { useMemo, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { withAlpha } from '../../lib/chartColors';
import { formatNumber } from '../../lib/formatMoney';
import { cn } from '../../lib/utils';
import { formatHeatmapHour, resolveHeatmapHours } from '../../lib/scanHeatmapUtils';

/** 4-stop navy scale: empty handled separately. */
const STOPS = [
  { bg: '#DCE6F5', fg: '#0F2F66', minRatio: 0 },
  { bg: '#4F7EC8', fg: '#ffffff', minRatio: 0.25 },
  { bg: '#1A428A', fg: '#ffffff', minRatio: 0.5 },
  { bg: '#0F2F66', fg: '#ffffff', minRatio: 0.75 },
];

const LABEL_RATIO = 0.25;

function getCellSurface(value, max) {
  if (!max || value <= 0) {
    return { bg: null, fg: '#cbd5e1', ratio: 0, showLabel: false, isEmpty: true };
  }
  const ratio = Math.min(1, value / max);
  let stop = STOPS[0];
  for (let i = STOPS.length - 1; i >= 0; i -= 1) {
    if (ratio >= STOPS[i].minRatio) {
      stop = STOPS[i];
      break;
    }
  }
  return {
    bg: stop.bg,
    fg: stop.fg,
    ratio,
    showLabel: ratio >= LABEL_RATIO,
    isEmpty: false,
  };
}

/**
 * Scan intensity board — house load strip + gate matrix as one composition.
 * Pattern-first; exact counts on hover / pinned column / high cells.
 */
export function ScanHeatmapBoard({ hours = [], gates = [], maxCell = 0, className }) {
  const { primaryColor } = useTheme();
  const brand = primaryColor || '#1A428A';
  const displayHours = useMemo(() => resolveHeatmapHours(hours, gates), [hours, gates]);

  const [pinnedHour, setPinnedHour] = useState(null);
  const [hovered, setHovered] = useState(null);

  const gridTemplate = useMemo(
    () => `minmax(8rem, 10rem) repeat(${displayHours.length}, minmax(1.65rem, 1fr))`,
    [displayHours.length]
  );

  const model = useMemo(() => {
    const byHour = displayHours.map((hour) =>
      gates.reduce((sum, gate) => sum + Number(gate.byHour?.[hour] ?? 0), 0)
    );
    const ribbonMax = Math.max(1, ...byHour);

    const gatePeaks = {};
    const gateTotals = {};
    let globalPeak = { gateId: null, hour: null, value: 0 };

    gates.forEach((gate) => {
      let peakHourLocal = null;
      let peakValue = 0;
      let total = 0;
      displayHours.forEach((hour) => {
        const value = Number(gate.byHour?.[hour] ?? 0);
        total += value;
        if (value > peakValue) {
          peakValue = value;
          peakHourLocal = hour;
        }
        if (value > globalPeak.value) {
          globalPeak = { gateId: gate.gateId, hour, value };
        }
      });
      gatePeaks[gate.gateId] = { hour: peakHourLocal, value: peakValue };
      gateTotals[gate.gateId] = total;
    });

    return { byHour, ribbonMax, gatePeaks, gateTotals, globalPeak };
  }, [displayHours, gates]);

  if (!gates.length) return null;

  const previewHour = hovered?.hour ?? null;
  const focusHour = pinnedHour;
  const tip = hovered
    ? (() => {
        const gate = gates.find((g) => g.gateId === hovered.gateId);
        if (!gate) return null;
        const value = Number(gate.byHour?.[hovered.hour] ?? 0);
        const hourTotal = model.byHour[displayHours.indexOf(hovered.hour)] || 0;
        const share = hourTotal > 0 ? Math.round((value / hourTotal) * 100) : 0;
        const isPeak =
          model.gatePeaks[gate.gateId]?.hour === hovered.hour && value > 0;
        return {
          gateName: gate.gateName,
          hour: hovered.hour,
          value,
          share,
          isPeak,
          isGlobal:
            model.globalPeak.gateId === gate.gateId &&
            model.globalPeak.hour === hovered.hour,
        };
      })()
    : null;

  function togglePin(hour) {
    setPinnedHour((current) => (current === hour ? null : hour));
  }

  return (
    <div className={cn('relative min-w-0', className)}>
      <div className="overflow-x-auto rounded-xl border border-slate-200/90 bg-[#FAFBFC]">
        <div className="min-w-full p-2 sm:p-2.5">
          {/* House load — same columns as matrix */}
          <div className="mb-1 grid items-end gap-x-0.5" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="sticky left-0 z-[1] flex items-end bg-[#FAFBFC] pb-1 pr-2">
              <p className="font-display text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                House load
              </p>
            </div>
            {displayHours.map((hour, index) => {
              const total = model.byHour[index];
              const ratio = total / model.ribbonMax;
              const isPinned = focusHour === hour;
              const isPreview = previewHour === hour && !isPinned;
              return (
                <button
                  key={`load-${hour}`}
                  type="button"
                  onClick={() => togglePin(hour)}
                  onMouseEnter={() => setHovered({ gateId: null, hour })}
                  onMouseLeave={() => setHovered(null)}
                  className={cn(
                    'flex h-9 flex-col items-center justify-end rounded-sm px-px pb-0.5 transition',
                    isPinned && 'bg-[var(--brand-primary,#1A428A)]/10',
                    isPreview && 'bg-slate-200/50'
                  )}
                  title={`${formatHeatmapHour(hour)} · ${formatNumber(total)} house scans`}
                  aria-pressed={isPinned}
                  aria-label={`House load ${formatHeatmapHour(hour)}, ${formatNumber(total)} scans`}
                >
                  <span
                    className="w-full max-w-[1.1rem] rounded-[2px]"
                    style={{
                      height: `${Math.max(4, Math.round(ratio * 28))}px`,
                      backgroundColor: withAlpha(brand, 0.3 + ratio * 0.55),
                    }}
                  />
                </button>
              );
            })}
          </div>

          {/* Hour labels */}
          <div className="mb-0.5 grid gap-x-0.5" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="sticky left-0 z-[1] bg-[#FAFBFC]" />
            {displayHours.map((hour) => {
              const isPinned = focusHour === hour;
              const isPreview = previewHour === hour;
              return (
                <button
                  key={`head-${hour}`}
                  type="button"
                  onClick={() => togglePin(hour)}
                  className={cn(
                    'flex h-5 items-end justify-center pb-0.5 font-display text-[9px] font-semibold tabular-nums tracking-wide transition',
                    isPinned
                      ? 'text-[var(--brand-primary,#1A428A)]'
                      : isPreview
                        ? 'text-slate-600'
                        : 'text-slate-400'
                  )}
                  aria-label={`Pin hour ${formatHeatmapHour(hour)}`}
                >
                  {String(hour).padStart(2, '0')}
                </button>
              );
            })}
          </div>

          {/* Gate matrix */}
          <div
            className="grid gap-x-0.5 gap-y-0.5"
            style={{ gridTemplateColumns: gridTemplate }}
            role="grid"
            aria-label="Scan load by gate and hour"
          >
            {gates.map((gate) => {
              const peak = model.gatePeaks[gate.gateId];
              const total = model.gateTotals[gate.gateId] || 0;

              return (
                <React.Fragment key={gate.gateId}>
                  <div className="sticky left-0 z-[1] flex min-h-[3.25rem] flex-col justify-center bg-[#FAFBFC] pr-2">
                    <p className="truncate text-[12px] font-semibold leading-tight tracking-tight text-slate-800">
                      {gate.gateName}
                    </p>
                    <p className="mt-0.5 text-[10px] tabular-nums text-slate-400">
                      {formatNumber(total)}
                    </p>
                  </div>

                  {displayHours.map((hour) => {
                    const value = Number(gate.byHour?.[hour] ?? 0);
                    const surface = getCellSurface(value, maxCell);
                    const isPinnedCol = focusHour === hour;
                    const isPreviewCol = previewHour === hour && !isPinnedCol;
                    const isHovered =
                      hovered?.gateId === gate.gateId && hovered?.hour === hour;
                    const isGatePeak = peak?.hour === hour && peak.value > 0;
                    const isGlobalPeak =
                      model.globalPeak.gateId === gate.gateId &&
                      model.globalPeak.hour === hour &&
                      model.globalPeak.value > 0;

                    // Focus priority: pinned > hover cell > peak (navy-only)
                    const showLabel =
                      surface.showLabel ||
                      isHovered ||
                      isPinnedCol ||
                      (isGatePeak && value > 0);

                    const loudPinned = isPinnedCol;
                    const loudHover = isHovered && !loudPinned;

                    return (
                      <div
                        key={`${gate.gateId}-${hour}`}
                        role="gridcell"
                        tabIndex={0}
                        onMouseEnter={() => setHovered({ gateId: gate.gateId, hour })}
                        onMouseLeave={() => setHovered(null)}
                        onFocus={() => setHovered({ gateId: gate.gateId, hour })}
                        onBlur={() => setHovered(null)}
                        onClick={() => togglePin(hour)}
                        className={cn(
                          'relative flex h-[3.25rem] cursor-pointer items-center justify-center rounded-[3px] text-[11px] font-semibold tabular-nums transition duration-100',
                          surface.isEmpty && 'bg-white ring-1 ring-inset ring-slate-100/90',
                          loudPinned && 'z-[1] ring-2 ring-[var(--brand-primary,#1A428A)]/50',
                          isPreviewCol && !loudPinned && 'ring-1 ring-slate-300/80',
                          loudHover && 'z-[2] scale-[1.03] shadow-sm'
                        )}
                        style={
                          surface.bg
                            ? {
                                backgroundColor: surface.bg,
                                color: surface.fg,
                                boxShadow:
                                  isGatePeak && !loudPinned
                                    ? `inset 0 0 0 1px ${withAlpha('#ffffff', isGlobalPeak ? 0.45 : 0.28)}`
                                    : undefined,
                              }
                            : undefined
                        }
                        aria-label={`${gate.gateName}, ${formatHeatmapHour(hour)}, ${formatNumber(value)} scans`}
                      >
                        {showLabel && value > 0 ? formatNumber(value) : null}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Compact legend + on-demand detail */}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <div className="flex h-1.5 overflow-hidden rounded-full">
            <span className="h-full w-3 bg-white ring-1 ring-inset ring-slate-200" />
            {STOPS.map((stop) => (
              <span key={stop.bg} className="h-full w-4" style={{ backgroundColor: stop.bg }} />
            ))}
          </div>
          <span className="text-slate-400">Load</span>
        </div>

        {tip && tip.gateName ? (
          <p className="ml-auto text-[12px] text-slate-600">
            <span className="font-semibold text-slate-800">{tip.gateName}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            {formatHeatmapHour(tip.hour)}
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="font-semibold tabular-nums text-slate-900">
              {formatNumber(tip.value)}
            </span>
            {tip.value > 0 ? (
              <span className="text-slate-400"> · {tip.share}% of hour</span>
            ) : null}
            {tip.isGlobal ? (
              <span className="text-slate-400"> · house peak</span>
            ) : tip.isPeak ? (
              <span className="text-slate-400"> · gate peak</span>
            ) : null}
          </p>
        ) : pinnedHour != null ? (
          <p className="ml-auto text-[11px] tabular-nums text-slate-500">
            {formatHeatmapHour(pinnedHour)}
            <span className="mx-1 text-slate-300">·</span>
            {formatNumber(model.byHour[displayHours.indexOf(pinnedHour)] || 0)} house
          </p>
        ) : null}
      </div>
    </div>
  );
}
